import type { CrowdexCollaboration, CrowdexUIMessage } from '@crowdex/agent-contracts';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { useEffect, useEffectEvent, useState } from 'react';
import { errorMessage } from '@/lib/error-message';
import {
  type ConversationWorktree,
  type LocalConversation,
  type LocalWorkspace,
  saveLocalWorkspace,
} from '@/lib/local-conversations';

async function post<TData>(url: string, body: unknown): Promise<TData> {
  return apiFetch<TData>(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Records a folder of this machine on its server, which names it (a worktree after its
 * repository, in its project) and links a worktree to its repository. `name` only names a folder
 * new to the server; `logicalWorkspaceId` is the project it joins.
 */
export async function associateDesktopWorkspace(
  workspaceRoot: string,
  options: { name?: string; logicalWorkspaceId?: string; runtime?: 'local' | 'worktree' } = {},
): Promise<LocalWorkspace & { registered: true }> {
  const saved = await saveLocalWorkspace({
    rootPath: workspaceRoot,
    ...(options.name === undefined ? {} : { name: options.name }),
    ...(options.logicalWorkspaceId === undefined
      ? {}
      : { logicalWorkspaceId: options.logicalWorkspaceId }),
    ...(options.runtime === undefined ? {} : { runtime: options.runtime }),
  });
  // Only the portable project identity is shared. The device path stays on the local server.
  // Registration is best-effort so selecting a local folder remains offline-first.
  await ensureCloudLogicalWorkspace(saved.logicalWorkspaceId, saved.name).catch(() => undefined);
  return { ...saved, registered: true };
}

export function ensureCloudLogicalWorkspace(id: string, name: string): Promise<unknown> {
  return post('/api/sync/logical-workspaces', { id, name });
}

/** A server-persisted local conversation, as the sidebar records it. */
export type LocalSavedThread = {
  id: string;
  workspaceId: string;
  logicalWorkspaceId: string | null;
  /** Its workspace's folder and name (the server's: a worktree's is its repository's). */
  workspaceRoot: string;
  workspaceName: string;
  /** Its own worktree, which it may have left for the main checkout. */
  worktree: ConversationWorktree | null;
  title: string;
  collaboration: CrowdexCollaboration;
  modelId: string | null;
  lastMessageAt: string | null;
  messageRevision: number;
  messageCount: number;
  runtime: 'local' | 'worktree';
};

/**
 * Refreshes the revision produced by server-side persistence without competing
 * writes, once a turn settles. Cloud conversations are read from the account
 * (`/api/sync/messages`), local ones from this machine's server.
 */
export function useServerMessageRevision(options: {
  enabled: boolean;
  settled: boolean;
  threadId: string;
  messageCount: number;
  source?: 'cloud' | 'local';
  onLoaded: (state: { revision: number; messageCount: number }) => void;
  onLocalSaved?: (saved: LocalSavedThread) => void;
  onMessagesLoaded?: (messages: readonly CrowdexUIMessage[]) => void;
}): string | null {
  const {
    enabled,
    settled,
    threadId,
    messageCount,
    source = 'cloud',
    onLoaded,
    onLocalSaved,
    onMessagesLoaded,
  } = options;
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const loaded = useEffectEvent(onLoaded);
  const localSaved = useEffectEvent((saved: LocalSavedThread) => onLocalSaved?.(saved));
  const messagesLoaded = useEffectEvent((messages: readonly CrowdexUIMessage[]) =>
    onMessagesLoaded?.(messages),
  );
  useEffect(() => {
    if (!enabled || !settled) return;
    const reload = () => setRefresh((current) => current + 1);
    window.addEventListener('focus', reload);
    window.addEventListener('online', reload);
    return () => {
      window.removeEventListener('focus', reload);
      window.removeEventListener('online', reload);
    };
  }, [enabled, settled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(refresh): the window regained focus or the network: the messages are read again.
  useEffect(() => {
    if (!enabled || !settled || messageCount === 0) return;
    const abort = new AbortController();
    const load =
      source === 'local'
        ? apiFetch<LocalConversation | null>(
            `/api/local/conversations/${encodeURIComponent(threadId)}`,
            { credentials: 'include', signal: abort.signal },
          ).then((saved) => {
            if (saved === null || abort.signal.aborted) return;
            messagesLoaded(
              saved.messages.map(({ metadata, ...message }) => ({
                ...message,
                ...(metadata == null ? {} : { metadata }),
              })),
            );
            loaded({ revision: saved.messageRevision, messageCount: saved.messages.length });
            localSaved({
              id: saved.id,
              workspaceId: saved.workspaceId,
              logicalWorkspaceId: saved.logicalWorkspaceId,
              workspaceRoot: saved.workspaceRoot,
              workspaceName: saved.workspaceName,
              worktree: saved.worktree,
              title: saved.title,
              collaboration: saved.collaboration,
              modelId: saved.modelId,
              lastMessageAt: saved.lastMessageAt,
              messageRevision: saved.messageRevision,
              messageCount: saved.messages.length,
              runtime: saved.runtime,
            });
          })
        : apiFetch<{ messages: readonly CrowdexUIMessage[]; revision: number }>(
            `/api/sync/messages?threadId=${encodeURIComponent(threadId)}`,
            { credentials: 'include', signal: abort.signal },
          ).then(
            (saved) => {
              if (abort.signal.aborted) return;
              messagesLoaded(saved.messages);
              loaded({ revision: saved.revision, messageCount: saved.messages.length });
            },
            (reason: unknown) => {
              // A new cloud conversation has no thread until its first turn starts: nothing
              // saved yet is not a failure.
              if ((reason as { code?: unknown } | null)?.code === 'not_found') return;
              throw reason;
            },
          );
    void load
      .then(() => {
        if (!abort.signal.aborted) setError(null);
      })
      .catch((reason: unknown) => {
        if (!abort.signal.aborted) setError(errorMessage(reason));
      });
    return () => abort.abort();
  }, [enabled, messageCount, refresh, settled, source, threadId]);

  return error;
}
